/**
 * Результат распознавания стен по подложке — без интерфейса и сторов: кандидаты в стены и
 * проёмы в мм, перевод из пикселей алгоритма «по заливке» и разрезание стен в стыках перед
 * постройкой. Чистые функции: их проверяют и в Node, на настоящих планах.
 */
import { THICKNESS_PRESETS } from "@/shared/catalog";
import { dist, projectOnSegment, segmentIntersection, type Pt } from "@/shared/lib/geometry";
import { uid, type OpeningKind } from "@/shared/plan";
import type { FillWall } from "./fillRecognition";

export interface RecognizedOpening {
	id: string;
	kind: OpeningKind;
	/** Центр проёма в мировых координатах, мм */
	center: Pt;
	/** Ширина проёма вдоль стены, мм */
	width: number;
	orientation: "h" | "v";
}

export interface RecognizedWall {
	id: string;
	from: Pt;
	to: Pt;
	thickness: number;
	bearing: boolean;
	enabled: boolean;
	/** Найденные в этой стене двери и окна (включая остекление балкона) */
	openings?: RecognizedOpening[];
}

/** Толщина стены — ближайшая из типовых. */
export const snapThickness = (rawMm: number): number => {
	let best: number = THICKNESS_PRESETS[0];
	let bestDiff = Math.abs(rawMm - best);
	for (const preset of THICKNESS_PRESETS) {
		const diff = Math.abs(rawMm - preset);
		if (diff < bestDiff) {
			best = preset;
			bestDiff = diff;
		}
	}
	return best;
};

/** Стена «по заливке» в пикселях картинки → кандидат в стены на плане, мм. */
export const fillWallToRecognized = (
	wall: FillWall,
	origin: Pt,
	mmPerPixel: number,
	includeOpenings: boolean,
): RecognizedWall => {
	const toMm = (px: number, base: number) => Math.round((base + px * mmPerPixel) / 10) * 10;
	const horizontal = wall.orientation === "h";
	const axis = horizontal ? toMm(wall.axis, origin.y) : toMm(wall.axis, origin.x);
	const start = toMm(wall.start, horizontal ? origin.x : origin.y);
	const end = toMm(wall.end, horizontal ? origin.x : origin.y);
	const thickness = snapThickness(Math.max(80, wall.thickness * mmPerPixel));
	const openings: RecognizedOpening[] = includeOpenings
		? wall.openings
				.map((opening) => {
					const from = toMm(opening.from, horizontal ? origin.x : origin.y);
					const to = toMm(opening.to, horizontal ? origin.x : origin.y);
					const middle = Math.round((from + to) / 20) * 10;
					return {
						id: uid("ro"),
						kind: opening.kind,
						center: horizontal ? { x: middle, y: axis } : { x: axis, y: middle },
						width: Math.round((to - from) / 10) * 10,
						orientation: wall.orientation,
					};
				})
				.filter((opening) => opening.width >= 450)
		: [];
	return {
		id: uid("rw"),
		from: horizontal ? { x: start, y: axis } : { x: axis, y: start },
		to: horizontal ? { x: end, y: axis } : { x: axis, y: end },
		thickness,
		// Несущая или нет — решает человек: распознанное пока всё перегородки
		bearing: false,
		enabled: true,
		openings,
	};
};

/** Ближе этого точки считаем одной, мм: распознанные концы округлены до 10 мм. */
const JUNCTION_TOLERANCE = 15;

/**
 * Разрезать стены во всех точках, где их касается или пересекает другая стена: крест
 * становится четырьмя стенами в узле, Т-стык — двумя по разные стороны. Тогда стены
 * встречаются только концами, и модель строит их все. Кусок, который ложится вдоль уже
 * взятого (дубль той же стены), отбрасываем.
 */
export const splitAtJunctions = (walls: RecognizedWall[]): RecognizedWall[] => {
	const pieces: RecognizedWall[] = [];
	for (const wall of walls) {
		const length = dist(wall.from, wall.to);
		if (length < JUNCTION_TOLERANCE) continue;
		const cuts = [0, 1];
		const cutAt = (point: Pt) => {
			const projection = projectOnSegment(point, wall.from, wall.to);
			if (projection.distance > JUNCTION_TOLERANCE) return;
			const along = projection.t * length;
			if (along > JUNCTION_TOLERANCE && along < length - JUNCTION_TOLERANCE) {
				cuts.push(projection.t);
			}
		};
		for (const other of walls) {
			if (other === wall) continue;
			cutAt(other.from);
			cutAt(other.to);
			const hit = segmentIntersection(wall.from, wall.to, other.from, other.to);
			if (hit && hit.t > 0 && hit.t < 1 && hit.u >= 0 && hit.u <= 1) cutAt(hit.point);
		}
		const sorted = [...new Set(cuts)].toSorted((a, b) => a - b);
		for (let i = 0; i < sorted.length - 1; i++) {
			if ((sorted[i + 1] - sorted[i]) * length < JUNCTION_TOLERANCE) continue;
			const at = (t: number): Pt => ({
				x: Math.round(wall.from.x + (wall.to.x - wall.from.x) * t),
				y: Math.round(wall.from.y + (wall.to.y - wall.from.y) * t),
			});
			const piece = { ...wall, id: uid("rw"), from: at(sorted[i]), to: at(sorted[i + 1]) };
			const middle = {
				x: (piece.from.x + piece.to.x) / 2,
				y: (piece.from.y + piece.to.y) / 2,
			};
			const duplicate = pieces.some(
				(kept) =>
					projectOnSegment(middle, kept.from, kept.to).distance < JUNCTION_TOLERANCE &&
					projectOnSegment(piece.from, kept.from, kept.to).distance < JUNCTION_TOLERANCE &&
					projectOnSegment(piece.to, kept.from, kept.to).distance < JUNCTION_TOLERANCE,
			);
			if (!duplicate) pieces.push(piece);
		}
	}
	return pieces;
};
